/**
 * Access gate shared by the game and its server (the Cloudflare Worker, and the optional Node LAN
 * server). The game must answer a fresh challenge from the server before anything runs, and then
 * carries the short-lived pass the server issues on every lobby and signalling request.
 *
 * There is no key string anywhere: both sides compute the same 32 bytes with `forge()`. Anyone with
 * the code can of course reproduce it; this keeps out casual copies and other sites using the
 * service, it is not a security boundary. Self-contained SHA-256 so it also works on plain-http LAN
 * addresses, where browsers do not provide crypto.subtle.
 */

// ---------- SHA-256 / HMAC (small, dependency-free) ----------
const K = new Uint32Array(64);
{
  // Round constants: fractional parts of the cube roots of the first 64 primes.
  let n = 0;
  for (let c = 2; n < 64; c++) {
    let prime = true;
    for (let d = 2; d * d <= c; d++) if (c % d === 0) prime = false;
    if (prime) K[n++] = (Math.cbrt(c) % 1) * 2 ** 32;
  }
}

function sha256(msg: Uint8Array): Uint8Array {
  const h = new Uint32Array([0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19]);
  const len = msg.length;
  const total = ((len + 9 + 63) >> 6) << 6;
  const buf = new Uint8Array(total);
  buf.set(msg);
  buf[len] = 0x80;
  const bits = len * 8;
  const dv = new DataView(buf.buffer);
  dv.setUint32(total - 4, bits >>> 0);
  dv.setUint32(total - 8, Math.floor(bits / 2 ** 32));
  const w = new Uint32Array(64);
  const rotr = (x: number, r: number) => (x >>> r) | (x << (32 - r));
  for (let off = 0; off < total; off += 64) {
    for (let i = 0; i < 16; i++) w[i] = dv.getUint32(off + i * 4);
    for (let i = 16; i < 64; i++) {
      const s0 = rotr(w[i - 15], 7) ^ rotr(w[i - 15], 18) ^ (w[i - 15] >>> 3);
      const s1 = rotr(w[i - 2], 17) ^ rotr(w[i - 2], 19) ^ (w[i - 2] >>> 10);
      w[i] = (w[i - 16] + s0 + w[i - 7] + s1) >>> 0;
    }
    let [a, b, c, d, e, f, g, hh] = h;
    for (let i = 0; i < 64; i++) {
      const t1 = (hh + (rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25)) + ((e & f) ^ (~e & g)) + K[i] + w[i]) >>> 0;
      const t2 = ((rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22)) + ((a & b) ^ (a & c) ^ (b & c))) >>> 0;
      hh = g; g = f; f = e; e = (d + t1) >>> 0; d = c; c = b; b = a; a = (t1 + t2) >>> 0;
    }
    h[0] += a; h[1] += b; h[2] += c; h[3] += d; h[4] += e; h[5] += f; h[6] += g; h[7] += hh;
  }
  const out = new Uint8Array(32);
  const ov = new DataView(out.buffer);
  for (let i = 0; i < 8; i++) ov.setUint32(i * 4, h[i]);
  return out;
}

const utf8 = (s: string) => new TextEncoder().encode(s);
const hex = (b: Uint8Array) => [...b].map((x) => x.toString(16).padStart(2, '0')).join('');

function hmac(key: Uint8Array, msg: string): string {
  const k = new Uint8Array(64);
  k.set(key.length > 64 ? sha256(key) : key);
  const inner = new Uint8Array(64 + utf8(msg).length);
  const outer = new Uint8Array(64 + 32);
  for (let i = 0; i < 64; i++) {
    inner[i] = k[i] ^ 0x36;
    outer[i] = k[i] ^ 0x5c;
  }
  inner.set(utf8(msg), 64);
  outer.set(sha256(inner), 64);
  return hex(sha256(outer));
}

/** The shared secret, computed rather than stored. */
function forge(): Uint8Array {
  const seedText = [70, 111, 117, 114, 32, 67, 114, 111, 119, 110, 115].map((c) => String.fromCharCode(c)).join('');
  let x = 0x9e3779b9;
  for (let i = 0; i < seedText.length; i++) x = Math.imul(x ^ seedText.charCodeAt(i), 0x85ebca6b) ^ (x >>> 13);
  const out = new Uint8Array(32);
  for (let i = 0; i < 32; i++) {
    x ^= x << 13;
    x ^= x >>> 17;
    x ^= x << 5;
    out[i] = (x >>> ((i % 4) * 8)) & 0xff;
  }
  return sha256(out);
}

const TOKEN_LIFE_MS = 24 * 60 * 60 * 1000;
const CHALLENGE_LIFE_MS = 5 * 60 * 1000;

// ---------- Server side ----------
export function makeChallenge(now: number, nonce: string): string {
  const body = `${nonce}.${now}`;
  return `${body}.${hmac(forge(), 'c:' + body)}`;
}

/** Check a challenge we issued (untampered, fresh) and the game's answer to it; returns a pass or null. */
export function redeem(challenge: string, answer: string, now: number): string | null {
  const parts = String(challenge).split('.');
  if (parts.length !== 3) return null;
  const [nonce, ts, sig] = parts;
  const t = Number(ts);
  if (!Number.isFinite(t) || now - t > CHALLENGE_LIFE_MS || t - now > 60_000) return null;
  if (hmac(forge(), 'c:' + nonce + '.' + ts) !== sig) return null;
  if (answerFor(challenge) !== answer) return null;
  const exp = now + TOKEN_LIFE_MS;
  return `${exp}.${hmac(forge(), 't:' + exp)}`;
}

export function passIsValid(pass: string | null | undefined, now: number): boolean {
  if (!pass) return false;
  const [exp, sig] = String(pass).split('.');
  const e = Number(exp);
  return Number.isFinite(e) && e > now && hmac(forge(), 't:' + exp) === sig;
}

// ---------- Game side ----------
export function answerFor(challenge: string): string {
  return hmac(forge(), 'a:' + challenge);
}

let pass: string | null = null;

/** The pass for this session (null until the gate has been passed). */
export function currentPass(): string | null {
  return pass;
}

/** The build the server says is live (0 until it has said). */
let live = 0;
export const liveBuild = (): number => live;

/**
 * Ask the server for a challenge (telling it which build this is; it answers with the live build),
 * answer the challenge and keep the pass. Resolves true when let in.
 */
export async function openGate(build = 0): Promise<boolean> {
  try {
    const r = await fetch(`/api/gate?b=${build}`, { cache: 'no-store' });
    if (!r.ok) return false;
    const { c, latest } = (await r.json()) as { c?: string; latest?: number };
    if (typeof latest === 'number') live = latest;
    if (!c) return false;
    const v = await fetch('/api/gate', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ c, a: answerFor(c) }) });
    if (!v.ok) return false;
    const { p } = (await v.json()) as { p?: string };
    pass = p ?? null;
    return !!pass;
  } catch {
    return false;
  }
}

/** Ask again which build is live (for a long session). Resolves to the live build, or 0 if unknown. */
export async function checkLiveBuild(build = 0): Promise<number> {
  try {
    const r = await fetch(`/api/gate?b=${build}`, { cache: 'no-store' });
    const { latest } = (await r.json()) as { latest?: number };
    if (typeof latest === 'number') live = latest;
  } catch {
    /* offline: keep what we had */
  }
  return live;
}

/** Test hook: the self-check vector (compare against a known SHA-256 output). */
export const _sha256Hex = (s: string) => hex(sha256(utf8(s)));
